import React from 'react';
import { useBackdropDismiss } from '../../utils/backdropDismiss';
import { formatMoney } from '../../utils/formatMoney';
import { X, ShoppingBag, Truck, RotateCcw, Trash2, Clock } from 'lucide-react';
import type { ParkedOrder } from '../../hooks/useOrderQueue';

interface Props {
  queue: ParkedOrder[];
  onResume: (id: number) => void;
  onRemove: (id: number) => void;
  onClose: () => void;
  title: string;
}

function elapsed(iso: string): string {
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${s}″`;
  if (s < 3600) return `${Math.floor(s / 60)}′`;
  return `${Math.floor(s / 3600)}h`;
}

const OrderQueueModal: React.FC<Props> = ({ queue, onResume, onRemove, onClose, title }) => {
  const backdrop = useBackdropDismiss(onClose);
  return (
    <div
      className="cashier-overlay fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
      {...backdrop}
    >
      <div className="cashier-dialog bg-surface-2 dark:bg-surface-1 border border-line rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
        {/* Header */}
        <div className="cashier-dialog-header flex items-center justify-between px-5 py-4 border-b border-line">
          <div className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-amber-700 dark:text-amber-400" />
            <h3 className="text-lg font-bold text-t1">{title}</h3>
            <span className="ml-1 bg-amber-500 text-black text-xs font-bold rounded-full px-2 py-0.5">
              {queue.length}
            </span>
          </div>
          <button
            onClick={onClose}
            className="text-t3 hover:text-t1 transition-colors p-1"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* List */}
        <div className="divide-y divide-line max-h-[60vh] overflow-y-auto">
          {queue.map(order => (
            <div key={order.id} className="flex items-center gap-3 px-5 py-4 hover:bg-surface-3 dark:hover:bg-surface-2/50 transition-colors group">
              {/* Icon */}
              <div className="flex-shrink-0 w-9 h-9 rounded-xl flex items-center justify-center bg-surface-2">
                {order.type === 'takeaway'
                  ? <ShoppingBag className="h-4 w-4 text-amber-700 dark:text-amber-400" />
                  : <Truck className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                }
              </div>

              {/* Info */}
              <button
                onClick={() => { onResume(order.id); onClose(); }}
                className="flex-1 text-left"
              >
                <p className="text-sm font-semibold text-t1">{order.label}</p>
                <div className="flex items-center gap-2 mt-0.5">
                  {order.total > 0 && (
                    <span className="text-xs text-amber-800 dark:text-amber-300">{formatMoney(Number(order.total))}</span>
                  )}
                  {order.item_count > 0 && (
                    <span className="text-xs text-t3">{order.item_count} τεμ.</span>
                  )}
                  <span className="text-xs text-t4">{elapsed(order.created_at)}</span>
                  {order.notes && (
                    <span className="text-xs text-t3 italic truncate max-w-[120px]">{order.notes}</span>
                  )}
                </div>
              </button>

              {/* Actions */}
              <div className="flex items-center gap-1 flex-shrink-0">
                <button
                  onClick={() => { onResume(order.id); onClose(); }}
                  className="p-2 rounded-lg text-amber-700 dark:text-amber-400 hover:bg-amber-200 dark:hover:bg-amber-900/40 hover:text-amber-800 dark:hover:text-amber-300 transition-colors"
                  title="Επαναφορά"
                >
                  <RotateCcw className="h-4 w-4" />
                </button>
                <button
                  onClick={() => onRemove(order.id)}
                  className="p-2 rounded-lg text-t3 dark:text-t4 hover:bg-red-100 dark:hover:bg-red-900/30 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                  title="Διαγραφή"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default OrderQueueModal;
